// THE FIXED PROPERTY SET (owner law, said more than once): every page shows
// the SAME rows whether or not the data is there. An empty row wears an em
// dash; it never disappears, so the shape of a word is legible at a glance
// and a missing property is visibly missing.
export const PROPERTIES = [
  { id: 'created',  label: 'Created' },
  { id: 'edited',   label: 'Last edited' },
  { id: 'usedIn',   label: 'Used in' },
  // HOW OFTEN (owner call 2026-10-04): the word's uses across the ecosystem —
  // Ink's notes today — and its synonyms' beside them
  { id: 'usage',    label: 'Usage' },
  { id: 'tags',     label: 'Tags' },
  // THE ENTRY'S OTHER FORMS (plural, tense, spelling): strings on the word
  // (owner correction 2026-10-05) — not words of their own
  { id: 'aliases',  label: 'Aliases' },
  // THE LINKS (owner call 2026-10-04): other words, both ways, added by hand
  { id: 'synonyms', label: 'Synonyms' },
  { id: 'antonyms', label: 'Antonyms' },
  { id: 'commitment', label: 'Commitment' },
  // THE STANCE AND WHAT YOU'D SAY INSTEAD (owner call 2026-09-30, the
  // anti-dictionary): a word is held or avoided; an avoided one names the
  // words you would rather use, and a held one shows what it stands in for
  { id: 'stance', label: 'Stance' },
  { id: 'rather', label: 'Rather' },
  // the ONE held word an anti-word is translated into, wherever text is written
  // for you (dictation, a persona, material read in); optional (owner call 2026-09-30)
  { id: 'replacement', label: 'Replace with' },
]
export const avoided = w => w && w.stance === 'avoided'
/* (The TYPE row and its five kinds — lexical, stipulative, precising,
   theoretical, persuasive — ripped out 2026-09-19, owner call: "people can
   just add their own tags". A word's dkind may still arrive from the wire;
   nothing here reads it.) */

export const dateLabel = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(+d)) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/* WHEN, TO THE SECOND (owner call 2026-09-18: "created time and edited time
   need a full timestamp including seconds — it's completely useless showing
   just date and month"). A date alone cannot tell two edits of the same
   afternoon apart, which is the only question a Last edited row is ever asked.
   The month stays short and the clock is the reader's own. */
export const stampLabel = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(+d)) return ''
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  })
}

/* the A·B·C spine: the list is grouped by first letter, as a dictionary is */
/* One group per letter even when the list is not perfectly in order (audit
   2026-10-02: a row appended unsorted — a new word, a reply from the wire —
   opened a second "#" group, two siblings with one React key). A word is
   filed by `nameOf` (its name, unless the list holds it by another). */
export function byLetter(words, nameOf = w => w.name) {
  const groups = new Map()
  for (const w of words) {
    const first = (nameOf(w)[0] || '#').toUpperCase()
    const head = /[A-Z]/.test(first) ? first : '#'
    if (!groups.has(head)) groups.set(head, { letter: head, words: [] })
    groups.get(head).words.push(w)
  }
  return [...groups.values()]
}

/* `from` narrows by whose word it is: '' is anyone, 'you' your own, else a
   friend's user id. 'shared' is a standing like the others — every word a
   friend shared with you, whatever it stands as. (Types are gone; nothing
   narrows by dkind.) */
export const filterWords = (words, { query, standing, tags = [], from = '' }) => words.filter(w => {
  // ANTI-WORDS STAND APART (owner call 2026-09-30): "Every word" is the
  // vocabulary you hold; the words you refuse show only under Anti-words — the
  // anti-dictionaries' own lens
  if ((standing === 'avoided') !== avoided(w)) return false
  if (standing !== 'avoided' && (standing === 'shared' ? !w.owner : (standing !== 'all' && w.status !== standing))) return false
  if (from === 'you' ? !!w.owner : (from && (!w.owner || w.owner.user_id !== from))) return false
  // any of the chosen tags — narrowing by tag is a lens, not a conjunction
  if (tags.length && !tags.some(t => (w.tags || []).includes(t))) return false
  if (!query) return true
  const q = query.toLowerCase()
  // the PLAIN mirror, never the markup — a definition is a document now
  return w.name.toLowerCase().includes(q) || (w.text || '').toLowerCase().includes(q)
    || (w.aliases || []).some(a => a.toLowerCase().includes(q))
    || (w.synonyms || []).some(r => r.term.toLowerCase().includes(q))
})

/* A SYNONYM'S DEFINITION, UNTIL IT HAS ITS OWN (owner call 2026-10-04): a word
   with no words of its own reads the first of its synonyms that has some —
   ONE hop, never further (A~B and B~C do not make A~C) — said as such, "via
   B". The word that writes its own shows its own. Only a word this list can
   read lends its definition; a link into another dictionary's graph cannot. */
export const inheritedOf = (word, words) => {
  if (!word || (word.text || '').trim()) return null
  for (const r of word.synonyms || []) {
    if (r.group || r.user) continue
    const w = words.find(x => x.id === r.id)
    if (w && (w.text || '').trim()) return { id: w.id, name: w.name, slug: w.slug, body: w.body, text: w.text }
  }
  return null
}
/* the words a link names, as the list holds them: each reference with its word, when it is here */
export const linkedOf = (word, field, words) => (word?.[field] || []).map(r => ({ ...r, word: r.group || r.user ? null : words.find(w => w.id === r.id) || null }))
